<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { api, ApiError } from '../../api/client'
import type { SubtitleInput } from '../../api/types'
import { IconTrash, IconUpload } from '../Icons'
import Modal from '../Modal.vue'
import TextPreviewModal from '../TextPreviewModal.vue'
import { useToast } from '../../lib/toast'
import { Badge, Button, Input, cx } from '../ui'
import { parseSubtitleName, readTextFile } from '../../lib/parse'
import { localeName } from '../../lib/format'

const MAX_BODY = 8 << 20
const MAX_LOCALES = 50
const ACCEPT = '.srt,.vtt,.txt,.ass,.ssa'

interface Draft {
  locale: string
  fileName: string
  content: string
  source: string
}

const props = defineProps<{
  open: boolean
  showId: number
  season: number
  episode: number
  existingLocales: string[]
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

const toast = useToast()
const drafts = ref<Draft[]>([])
const error = ref('')
const busy = ref(false)
const previewIndex = ref<number | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

watch(
  () => [props.open, props.season, props.episode] as const,
  ([open]) => {
    if (!open) return
    error.value = ''
    drafts.value = []
    previewIndex.value = null
  },
)

function byteLength(list: Draft[]): number {
  return list.reduce((sum, d) => sum + new Blob([d.content]).size, 0)
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function validate(list: Draft[]): string {
  if (list.length === 0) return '至少一条字幕'
  if (list.length > MAX_LOCALES) return `最多 ${MAX_LOCALES} 种语言`
  if (byteLength(list) > MAX_BODY) return `合计 ${humanSize(byteLength(list))}，超过 ${humanSize(MAX_BODY)}`
  const seen = new Set<string>()
  for (const [index, d] of list.entries()) {
    const locale = d.locale.trim().toLowerCase()
    if (!locale) return `第 ${index + 1} 条缺少语言`
    if (d.content.trim() === '') return `第 ${index + 1} 条内容为空`
    if (seen.has(locale)) return `语言重复：${locale}`
    seen.add(locale)
  }
  return ''
}

function toInputs(list: Draft[]): SubtitleInput[] {
  return list.map((d) => ({
    locale: d.locale.trim().toLowerCase(),
    ...(d.fileName.trim() ? { file_name: d.fileName.trim() } : {}),
    content: d.content,
  }))
}

function patch(index: number, key: keyof Draft, value: string) {
  drafts.value = drafts.value.map((row, i) => (i === index ? { ...row, [key]: value } : row))
}

async function onPickFiles(files: FileList | null) {
  if (!files || files.length === 0) return
  const loaded: Draft[] = []
  for (const file of Array.from(files)) {
    const parsed = parseSubtitleName(file.name, props.season)
    try {
      loaded.push({
        locale: parsed?.locale ?? '',
        fileName: file.name,
        content: await readTextFile(file),
        source: file.name,
      })
    } catch {
      error.value = `读取失败：${file.name}`
    }
  }
  if (loaded.length > 0) {
    drafts.value = [...drafts.value, ...loaded]
    error.value = ''
  }
  if (fileInput.value) fileInput.value.value = ''
}

async function submit() {
  const invalid = validate(drafts.value)
  if (invalid) {
    error.value = invalid
    return
  }
  busy.value = true
  error.value = ''
  try {
    const result = await api.uploadSubs(props.showId, props.season, props.episode, toInputs(drafts.value))
    toast.ok(`已上传 ${result.uploaded} 条`)
    emit('saved')
    emit('close')
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '上传失败'
  } finally {
    busy.value = false
  }
}

const preview = computed(() => (previewIndex.value !== null ? drafts.value[previewIndex.value] : null))
</script>

<template>
  <Modal
    :open="open"
    :title="`字幕 · S${String(season).padStart(2, '0')}E${String(episode).padStart(2, '0')}`"
    width="max-w-lg"
    @close="emit('close')"
  >
    <template #footer>
      <span class="mr-auto text-[12px] text-dim"> {{ drafts.length }} 条 · {{ humanSize(byteLength(drafts)) }} </span>
      <Button @click="emit('close')">取消</Button>
      <Button variant="primary" :loading="busy" :disabled="drafts.length === 0" @click="void submit()">
        <IconUpload :size="13" />
        上传
      </Button>
    </template>

    <div class="flex flex-col gap-3">
      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="ACCEPT"
        class="hidden"
        @change="(e) => void onPickFiles((e.target as HTMLInputElement).files)"
      />
      <Button size="sm" variant="primary" @click="fileInput?.click()">
        <IconUpload :size="13" />
        选择文件
      </Button>

      <p v-if="existingLocales.length > 0" class="text-[12px] text-dim">已有 {{ existingLocales.join('、') }}</p>

      <ul v-if="drafts.length > 0" class="divide-y divide-line rounded-md border border-line">
        <li v-for="(row, index) in drafts" :key="index" class="flex items-center gap-2 px-2.5 py-2">
          <Input
            :class="cx('max-w-[88px] font-mono text-[12px]', !row.locale.trim() && 'border-warn/60')"
            placeholder="en"
            :aria-label="`第 ${index + 1} 条语言`"
            :model-value="row.locale"
            @update:model-value="(v) => patch(index, 'locale', v)"
          />
          <button
            type="button"
            class="min-w-0 flex-1 truncate text-left font-mono text-[11.5px] text-link hover:underline"
            title="预览正文"
            @click="previewIndex = index"
          >
            {{ row.source }}
          </button>
          <Badge v-if="row.locale" tone="neutral">{{ localeName(row.locale.trim().toLowerCase()) }}</Badge>
          <Button
            size="sm"
            variant="ghost"
            :aria-label="`删除第 ${index + 1} 条`"
            @click="drafts = drafts.filter((_, i) => i !== index)"
          >
            <IconTrash :size="13" />
          </Button>
        </li>
      </ul>

      <p
        v-if="error"
        role="alert"
        class="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger"
      >
        {{ error }}
      </p>
    </div>
  </Modal>

  <TextPreviewModal
    :open="preview !== null"
    :title="preview?.locale ? `${preview.locale}` : '字幕预览'"
    :hint="preview?.source"
    :content="preview?.content ?? ''"
    @close="previewIndex = null"
  />
</template>
